import { useEffect, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { aiApiClient } from "@/lib/ai-api-client"

// ── Types ──────────────────────────────────────────────────────────

/** Where a generation stands in the review workflow (derived on the server from the run and its draft). */
export type GenerationState = "queued" | "running" | "review" | "scheduled" | "published" | "failed" | "discarded"
export type GenerationFilter = GenerationState | "all"

export const GENERATION_FILTERS: { value: GenerationFilter; label: string }[] = [
  { value: "review", label: "Needs review" },
  { value: "running", label: "Writing" },
  { value: "queued", label: "Queued" },
  { value: "scheduled", label: "Scheduled" },
  { value: "published", label: "Published" },
  { value: "failed", label: "Failed" },
  { value: "discarded", label: "Discarded" },
  { value: "all", label: "All" },
]

export interface GenerationListItem {
  id: number
  topic: string
  category: string | null
  post_type: string | null
  provider: string | null
  status: string
  stage: string | null
  progress: number
  post_id: number | null
  title: string | null
  focus_keyword: string | null
  seo_score: number | null
  word_count: number | null
  image_url: string | null
  error: string | null
  api_calls: number
  cache_hits: number
  input_tokens: number
  output_tokens: number
  /** Dollar cost priced per call from the serving model; 0 on runs from before cost tracking. */
  cost_usd?: number
  /** Model that served the run's latest call. */
  model?: string | null
  created_at: string
  updated_at: string
  reviewed_at: string | null
  post_slug: string | null
  post_status: string | null
  published_at: string | null
  state: GenerationState
  /** Byline of the draft (public name when set). */
  author_name?: string | null
  /** 1 for the first run; retries count up. */
  attempt?: number
  gate_passed?: boolean | number | null
}

export interface QualityCheck {
  /** Stable check id (see CHECK_LABELS in Settings → Publishing); missing on older runs. */
  id?: string
  name: string
  passed: boolean
  severity?: string
}

/** Findings of the final audit. Each entry is a list of examples, or a count on older runs. */
export interface QualityFlags {
  firstPersonClaims?: string[] | number
  unsupportedNumbers?: string[] | number
  unverifiedQuotes?: string[] | number
  chatterRemoved?: string[] | number
}

export interface ArticleSource {
  url: string
  title?: string | null
  domain?: string | null
}

export interface GenerationAuthor {
  id: number
  name: string
  publicName?: string | null
  avatar?: string | null
  /** A list; older APIs may return the raw JSON text of the authors table. */
  specialties?: string[] | string | null
}

export interface GenerationTag {
  id: number
  name: string
  slug: string
  /** Created by this generation (not an existing tag). */
  isNew?: boolean
}

/** Result of the publishing quality gate and what the publishing policy did with the post. */
export interface GenerationGate {
  passed: boolean
  reasons: string[]
  action?: "review" | "scheduled" | "published" | string | null
  publishAt?: string | null
  score?: number | null
  checkedAt?: string | null
}

/** Why the generator chose this author and category. */
export interface GenerationAssignment {
  authorReason?: string | null
  categoryReason?: string | null
  createdCategory?: boolean | null
}

export interface PlanSection {
  title: string
  purpose?: string
  points?: string[]
  representation: string
  representationNotes?: string
  subheadings?: string[]
  targetWords?: number
}

export interface GenerationPlan {
  format?: string
  post_type?: string
  schema_type?: string
  search_intent?: string
  audience?: string
  angle?: string
  target_words?: number
  key_takeaways?: string[]
  faq_questions?: string[]
  closing?: { heading?: string; approach?: string }
  intro?: { approach?: string; targetWords?: number }
  sections?: PlanSection[]
  secondary_keywords?: string[]
}

/** A sentence that repeats another page word for word (a verified run of `words` identical consecutive words). */
export interface OriginalityMatch {
  sentence: string
  words: number
  excerpt: string
  url: string
  title: string
  domain: string
  /** "source": a page the article was researched from; "web": found by the web check. */
  origin: "source" | "web"
}

export interface OriginalityReport {
  status: "passed" | "flagged" | "error"
  error?: string
  title?: { unique: boolean; clashWith: string | null; retitled: boolean; previous: string | null }
  matches?: OriginalityMatch[]
  overlapPercent?: number
  minRunWords?: number
  rewritten?: number
  sourcePages?: number
  web?: { status: "checked" | "off" | "unavailable" | "failed"; searches: number; sentences: number; pages?: number }
}

export interface GenerationDetail extends Omit<GenerationListItem, "title"> {
  title: string | null
  post_title: string | null
  post_image: string | null
  post_excerpt: string | null
  post_content: string | null
  post_read_time: string | null
  category_name: string | null
  subcategory_name?: string | null
  author?: GenerationAuthor | null
  tags?: GenerationTag[]
  gate?: GenerationGate | null
  assignment?: GenerationAssignment | null
  parent_id?: number | null
  research: string | null
  review_note: string | null
  options: Record<string, unknown> | null
  plan: GenerationPlan | null
  article: {
    format?: string
    quality?: {
      score: number
      seoScore?: number
      naturalScore?: number
      wordCount: number
      checks: QualityCheck[]
      writing?: Record<string, unknown>
      flags?: QualityFlags
    }
    usage?: { apiCalls: number; cacheHits: number; inputTokens: number; outputTokens: number }
    edited?: boolean
    /** 'applied' | 'rejected:<reason>' | 'failed' | 'failed:truncated' | 'disabled' */
    edit_status?: string
    sources?: ArticleSource[]
    research_used?: boolean
    /** Missing on posts written before the check existed (or with Settings → Content & Voice → Originality off). */
    originality?: OriginalityReport | null
  } | null
  seo: {
    title: string
    description: string
    keywords: string | null
    focusKeyword: string | null
    schemaType: string | null
    imageAlt: string | null
    faq: { question: string; answer: string }[]
    ogTitle?: string | null
    ogDescription?: string | null
    robots?: string | null
    sources?: ArticleSource[]
  } | null
}

export interface GenerationLogLine {
  id: number
  level: string
  message: string
  step: number | null
  timestamp: string | null
}

export const isActive = (state?: GenerationState | null) => state === "queued" || state === "running"

// ── Queries ────────────────────────────────────────────────────────

export function useGenerationSummary() {
  return useQuery({
    queryKey: ["ai-generations", "summary"],
    queryFn: async () => {
      const res = await aiApiClient.get<{ counts: Record<GenerationFilter, number>; queue: { queued: number; running: boolean } }>(
        "/api/admin/ai/generations/summary",
      )
      return { counts: res.counts, queued: res.queue?.queued ?? 0, running: !!res.queue?.running }
    },
    refetchInterval: (query) => (query.state.data?.running || (query.state.data?.queued ?? 0) > 0 ? 3_000 : 15_000),
  })
}

export function useGenerations(filter: GenerationFilter, search: string) {
  return useQuery({
    queryKey: ["ai-generations", "list", filter, search],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: "100" })
      if (filter !== "all") params.set("state", filter)
      if (search.trim()) params.set("q", search.trim())
      const res = await aiApiClient.get<{ generations: GenerationListItem[] }>(`/api/admin/ai/generations?${params}`)
      return res.generations || []
    },
    placeholderData: (previous) => previous,
    refetchInterval: (query) => ((query.state.data || []).some((g) => isActive(g.state)) ? 3_000 : 20_000),
  })
}

export function useGeneration(id: number) {
  return useQuery({
    queryKey: ["ai-generations", "detail", id],
    queryFn: async () => {
      const res = await aiApiClient.get<{ success: boolean; generation?: GenerationDetail; error?: string }>(
        `/api/admin/ai/generations/${id}`,
      )
      if (!res.success || !res.generation) throw new Error(res.error || "Generation not found")
      return res.generation
    },
    enabled: id > 0,
    refetchInterval: (query) => (isActive(query.state.data?.state) ? 2_500 : false),
  })
}

/** Incremental log for one generation: fetches only lines newer than the last one received. */
export function useGenerationLog(id: number, live: boolean) {
  const [lines, setLines] = useState<GenerationLogLine[]>([])
  const lastId = useRef(0)

  useEffect(() => {
    setLines([])
    lastId.current = 0
  }, [id])

  const query = useQuery({
    queryKey: ["ai-generations", "logs", id],
    queryFn: async () => {
      const res = await aiApiClient.get<{ logs: GenerationLogLine[] }>(
        `/api/admin/ai/generations/${id}/logs?after=${lastId.current}`,
      )
      const fresh = res.logs || []
      if (fresh.length) {
        lastId.current = fresh[fresh.length - 1].id
        setLines((prev) => [...prev, ...fresh])
      }
      return fresh.length
    },
    enabled: id > 0,
    refetchInterval: live ? 2_000 : false,
  })

  return { lines, isLoading: query.isLoading }
}

// ── Review actions ─────────────────────────────────────────────────

type ActionResult = { success: boolean; error?: string; state?: GenerationState; publishedAt?: string; generationId?: number }

function useGenerationAction<TVars, TResult extends { success: boolean; error?: string } = ActionResult>(
  request: (vars: TVars) => Promise<TResult>,
) {
  const queryClient = useQueryClient()
  return useMutation<TResult, Error, TVars>({
    mutationFn: async (vars) => {
      const res = await request(vars)
      if (!res.success) throw new Error(res.error || "Action failed")
      return res
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-generations"] })
      queryClient.invalidateQueries({ queryKey: ["posts"] })
      queryClient.invalidateQueries({ queryKey: ["trend-explorer"] })
      queryClient.invalidateQueries({ queryKey: ["content-calendar"] })
    },
  })
}

export interface BulkPublishResult {
  success: boolean
  error?: string
  /** Drafts that went live now. */
  published: number
  /** Drafts staggered into later posting slots (Settings → Publishing). */
  scheduled: Array<{ postId: number; publishAt: string }>
  /** Generation ids that were not waiting for review. */
  skipped: number[]
}

export interface BulkDiscardResult {
  success: boolean
  error?: string
  discarded: number
  skipped: Array<{ id: number; error?: string }>
}

/** What to bulk publish: generation ids plus, optionally, this batch's cadence (see ScheduleCadence). */
export interface BulkPublishInput {
  ids: number[]
  perDay?: number
  days?: string[]
  startDate?: string
}

/** Publishes the drafts of several generations into successive posting slots (news first), at the batch's cadence
 * when given, else as Settings → Publishing says. */
export function useBulkPublishGenerations() {
  return useGenerationAction<BulkPublishInput, BulkPublishResult>((input) =>
    aiApiClient.post<BulkPublishResult>("/api/admin/ai/generations/bulk-publish", input),
  )
}

/** Discards several generations and deletes their drafts. */
export function useBulkDiscardGenerations() {
  return useGenerationAction<number[], BulkDiscardResult>((ids) =>
    aiApiClient.post<BulkDiscardResult>("/api/admin/ai/generations/bulk-discard", { ids }),
  )
}

/** Drafts waiting for review: the badge on AI Generations (sidebar, mobile nav, dashboard). */
export function useReviewCount() {
  const summary = useGenerationSummary()
  return { ...summary, count: summary.data?.counts?.review ?? 0 }
}

export function usePublishGeneration() {
  return useGenerationAction<{ id: number; publishAt?: string; note?: string }>(({ id, ...body }) =>
    aiApiClient.post<ActionResult>(`/api/admin/ai/generations/${id}/publish`, body),
  )
}

export function useUnpublishGeneration() {
  return useGenerationAction<number>((id) => aiApiClient.post<ActionResult>(`/api/admin/ai/generations/${id}/unpublish`))
}

export function useDiscardGeneration() {
  return useGenerationAction<{ id: number; note?: string }>(({ id, ...body }) =>
    aiApiClient.post<ActionResult>(`/api/admin/ai/generations/${id}/discard`, body),
  )
}

export function useRetryGenerationRun() {
  return useGenerationAction<number>((id) => aiApiClient.post<ActionResult>(`/api/admin/ai/generations/${id}/retry`))
}

export interface GenerationTaxonomyInput {
  id: number
  categoryId?: number
  subcategoryId?: number | null
  authorId?: number
  tagIds?: number[]
}

/** Reassigns the draft's category, subcategory, author and tags from the review screen. */
export function useUpdateGenerationTaxonomy() {
  return useGenerationAction<GenerationTaxonomyInput>(({ id, ...body }) =>
    aiApiClient.post<ActionResult>(`/api/admin/ai/generations/${id}/taxonomy`, body),
  )
}
